import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doesPedalFullyMatchWant } from '../utils/matchingLogic';
import { findDuplicateWant, isSameNamedPedal } from '../utils/wantIdentity';
import {
  quickAddWantForListing,
  quickRemoveWantForListing,
  signedOutWantPrefillUrl
} from '../utils/quickWant';

/**
 * The demand heart (WANT_DEMAND_BADGE_PLAN.md, 2026-09-16): a frosted white
 * pill on a listing image showing how many people have the pedal in their
 * Wantlist. Outline heart always visible as the tap target; the number
 * appears from 1 up (Jason: no "0"). Tapping runs the exact same flow as the
 * listing page's "Save to Wantlist" button — the shared core in
 * src/utils/quickWant.js — filling the heart; tapping again removes the want.
 *
 * Two modes:
 *  - Card mode (default): pass `pedal`, `currentUser`, `firestoreUser`,
 *    `myWants` (from useMyWants — ONE listener per page, never per badge).
 *    The badge derives fill/conflict state and performs the add/remove
 *    itself. The duplicate-want conflict edge (same-model want with
 *    different settings) navigates to the listing page, where the existing
 *    conflict modal owns the decision — approved 2026-09-16.
 *  - Controlled mode (the listing page itself): pass `onPress` + `filled` +
 *    `busy`; the page's own handlers (with their conflict modal and prompt
 *    copy) do the work and this component only renders.
 *
 * The count is `pedal.wantCount` (denormalized by functions/listingWantCount.js)
 * plus a local optimistic bump — applied only when the viewer is fully
 * verified, because the server counter ignores unverified accounts and a bump
 * it will never confirm would visibly revert on the next load.
 */

const BLOCKED_ACCOUNT_STATUSES = ['banned', 'suspended_pending_review'];

function viewerCountsTowardDemand(firestoreUser) {
  if (!firestoreUser || firestoreUser.isVerified !== true) return false;
  return !BLOCKED_ACCOUNT_STATUSES.includes(firestoreUser.accountStatus || 'active');
}

function HeartIcon({ filled, size = 20 }) {
  return (
    <svg
      className="want-demand-heart"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? '#e0245e' : 'none'}
      stroke={filled ? '#e0245e' : '#374151'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

const WantDemandBadge = ({
  pedal,
  currentUser,
  firestoreUser,
  myWants = [],
  // Lifted position for cards whose bottom "Potential Match"-style banner is
  // showing, so the two never overlap (badge is bottom-right; the Active
  // Trader badge owns top-right).
  raised = false,
  // Controlled mode (listing page): the page's handlers own the behavior.
  onPress = null,
  filled: filledProp = undefined,
  busy: busyProp = undefined
}) => {
  const navigate = useNavigate();
  const [mutating, setMutating] = useState(false);
  const [optimisticDelta, setOptimisticDelta] = useState(0);
  // Immediate feedback (Stage 3, 2026-10-31). The heart used to disable
  // itself and change nothing until the want write AND the slower interest/
  // analytics tail had finished, which on a phone can be several seconds of
  // "did that do anything?". Now the fill and the count change on the tap:
  //  - `optimisticFilled` overrides the listener-derived fill until the
  //    wantlist listener catches up (then it is cleared, so there is no second
  //    flip and no flicker);
  //  - a tap while an operation is in flight is ignored (no overlapping
  //    add/remove, no double counting);
  //  - only the want write decides success: if it fails, the previous fill
  //    and count come back and the user sees the existing error; a failure in
  //    the later interest/analytics tail never undoes a saved want.
  const [optimisticFilled, setOptimisticFilled] = useState(null);
  const inFlightRef = useRef(false);

  const controlled = typeof onPress === 'function';
  const isOwnListing = Boolean(currentUser && pedal?.ownerId === currentUser.uid);

  // Card-mode fill/conflict state, derived from the page-level wants listener
  // with the same predicates the listing page uses (see the long comment in
  // PedalDetails' checkWantList effect for why full-match and identity-match
  // are separate tiers).
  const { matchedWant, hasConflict } = useMemo(() => {
    if (controlled || !currentUser || !pedal || isOwnListing) {
      return { matchedWant: null, hasConflict: false };
    }
    const full = myWants.find((want) => doesPedalFullyMatchWant(want, pedal)) || null;
    if (full) return { matchedWant: full, hasConflict: false };
    const partial =
      findDuplicateWant(myWants, { make: pedal.make, model: pedal.model }) ||
      myWants.find((want) => isSameNamedPedal(want, pedal)) ||
      null;
    return { matchedWant: null, hasConflict: Boolean(partial) };
  }, [controlled, currentUser, pedal, isOwnListing, myWants]);

  const listenerFilled = controlled ? Boolean(filledProp) : Boolean(matchedWant);
  // Reconcile with the wantlist listener: once it reports what we already
  // show, drop the override (no second flip, no flicker). If it reports the
  // opposite while nothing is in flight, the server state wins.
  useEffect(() => {
    if (optimisticFilled !== null && listenerFilled === optimisticFilled) setOptimisticFilled(null);
  }, [listenerFilled, optimisticFilled]);
  const filled = optimisticFilled !== null ? optimisticFilled : listenerFilled;
  const busy = controlled ? Boolean(busyProp) : mutating;
  const baseCount = Number.isFinite(pedal?.wantCount) ? pedal.wantCount : 0;
  // If the denormalized server count changes under us it already includes
  // our want: drop the local bump rather than double count.
  const baseCountRef = useRef(baseCount);
  useEffect(() => {
    if (baseCountRef.current !== baseCount) { baseCountRef.current = baseCount; setOptimisticDelta(0); }
  }, [baseCount]);
  const count = Math.max(0, baseCount + optimisticDelta);

  const handlePress = async (e) => {
    // The badge sits inside card links/click surfaces on every page — the
    // heart must never also open the listing.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;

    if (controlled) {
      onPress();
      return;
    }

    // Signed out: identical to the listing-page button — prefilled add-want
    // form. (React Router v6 does not auto-scroll on navigation.)
    if (!currentUser) {
      window.scrollTo(0, 0);
      navigate(signedOutWantPrefillUrl(pedal), {
        state: {
          tab: 'want',
          returnToListing: {
            id: pedal.id,
            label: `${pedal.make || ''} ${pedal.model || ''}`.trim() || 'this listing'
          }
        }
      });
      return;
    }

    if (isOwnListing) return;

    // Conflict edge: the listing page's modal owns this decision.
    if (!filled && hasConflict) {
      window.scrollTo(0, 0);
      navigate(`/listing/${pedal.id}`);
      return;
    }

    if (inFlightRef.current) return; // duplicate tap while an operation is in flight
    const bump = viewerCountsTowardDemand(firestoreUser) ? 1 : 0;
    const removing = filled;
    const wantIdToRemove = removing ? (matchedWant && matchedWant.id) : null;
    if (removing && !wantIdToRemove) return; // listener has not confirmed the want yet; nothing to remove
    inFlightRef.current = true;
    setMutating(true);
    // Feedback first.
    setOptimisticFilled(!removing);
    setOptimisticDelta((d) => d + (removing ? -bump : bump));
    let wantWritten = false;
    try {
      if (removing) {
        await quickRemoveWantForListing({ user: currentUser, pedal, wantId: wantIdToRemove });
        wantWritten = true;
      } else {
        await quickAddWantForListing({
          user: currentUser,
          pedal,
          // Fires as soon as the want document exists, before the interest and
          // analytics tail; from here on a tail failure must not roll back.
          onWantWritten: () => { wantWritten = true; }
        });
        wantWritten = true;
      }
    } catch (err) {
      if (wantWritten) {
        // The want is saved; only the follow-up work failed. Keep the state.
        console.error('⚠️ Want saved but a follow-up step failed:', err);
      } else {
        console.error('❌ Heart badge want toggle failed:', err);
        setOptimisticFilled(null);
        setOptimisticDelta((d) => d - (removing ? -bump : bump));
        alert(removing
          ? 'Could not remove from your Wantlist. Please try again.'
          : 'Could not add to your Wantlist. Please try again.');
      }
    } finally {
      inFlightRef.current = false;
      setMutating(false);
    }
  };

  if (!pedal) return null;

  const interactive = controlled || !isOwnListing;
  const label = filled
    ? `In your Wantlist — ${count} ${count === 1 ? 'person wants' : 'people want'} this pedal`
    : count > 0
      ? `${count} ${count === 1 ? 'person wants' : 'people want'} this pedal — add it to your Wantlist`
      : 'Save to your Wantlist';

  return (
    <button
      type="button"
      className="want-demand-badge"
      onClick={interactive ? handlePress : undefined}
      disabled={!interactive || busy}
      aria-label={label}
      title={label}
      style={{
        // 16px insets mirror the Active Trader badge (top-right, 16px), so
        // the two corners read as one system; `raised` clears the bottom
        // "Potential Match"/"Wants your X" ribbon (~43px tall incl. inset).
        position: 'absolute',
        right: '16px',
        bottom: raised ? '52px' : '16px',
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        gap: count > 0 ? '5px' : 0,
        minWidth: '36px',
        height: '36px',
        padding: count > 0 ? '0 11px' : 0,
        justifyContent: 'center',
        borderRadius: '18px',
        border: '1px solid rgba(255, 255, 255, 0.6)',
        // The frosted circle: translucent white over the photo, blurred where
        // supported; the fallback rgba alone stays legible without blur.
        background: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
        cursor: interactive && !busy ? 'pointer' : 'default',
        opacity: busy ? 0.6 : 1,
        transition: 'opacity 0.15s ease',
        fontFamily: 'inherit'
      }}
    >
      <HeartIcon filled={filled} />
      {count > 0 && (
        <span className="want-demand-count" style={{
          fontSize: '13px',
          fontWeight: 700,
          color: '#1f2937',
          lineHeight: 1
        }}>
          {count}
        </span>
      )}
    </button>
  );
};

export default WantDemandBadge;
